import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { getTareasLista, completarTarea, reabrirTarea } from '../../services/data';
import { hoyGT, fechaCorta } from '../finanzas/estados';
import { fechaGT, formatDateTime, formatTime, sumarDiasCalendario, sumarMesesCalendario } from '../../utils/dates';
import MyCalendar from '../calendario/MyCalendar';
import DepositosModal from '../finanzas/DepositosModal';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import '../finanzas/ui.css';
import './Agenda.css';

const fechaHora = formatDateTime;
const horaGT = formatTime;
const REPETICION = { Diario: 'diaria', Semanal: 'semanal', Mensual: 'mensual' };

/* ---------- Periodo de la Vista de agenda (desde hoy, fecha final inclusiva) ---------- */
const PERIODOS = [['hoy', 'Hoy'], ['7d', '7 días'], ['15d', '15 días'], ['1m', '1 mes'], ['2m', '2 meses'], ['3m', '3 meses'],
  ['6m', '6 meses'], ['1a', '1 año'], ['personalizado', 'Personalizado']];
const sumarDias = sumarDiasCalendario;
/** Mismo día N meses después; si ese mes no lo tiene (31 → 30/28), el último día del mes. */
const sumarMeses = sumarMesesCalendario;
function rangoPeriodo(periodo, hoy, manual) {
  const hasta = { hoy: hoy, '7d': sumarDias(hoy, 6), '15d': sumarDias(hoy, 14), '1m': sumarMeses(hoy, 1), '2m': sumarMeses(hoy, 2),
    '3m': sumarMeses(hoy, 3), '6m': sumarMeses(hoy, 6), '1a': sumarMeses(hoy, 12) }[periodo];
  return hasta ? { desde: hoy, hasta } : manual;
}
// El periodo se recuerda en la sesión del navegador (al abrir una tarea o cambiar de vista no se pierde).
const CLAVE_PERIODO = 'agenda.periodo';
const leerPeriodo = (hoy) => {
  try { const v = JSON.parse(sessionStorage.getItem(CLAVE_PERIODO) || 'null'); if (v?.periodo) return v; } catch {}
  return { periodo: '7d', desde: hoy, hasta: sumarDias(hoy, 29) };
};

/* ---------- Orden: atrasadas, hoy, próximas; luego prioridad (Alta, Media, Baja) y fecha ---------- */
const PRIORIDADES = ['Alta', 'Media', 'Baja']; // mismo orden que la API (routes/finanzas.js)
const grupoDe = (t, hoy) => { const f = fechaGT(t.inicioAt); return f < hoy ? 0 : f === hoy ? 1 : 2; };
const ESTADO_GRUPO = [['Atrasada', 'vencida'], ['Hoy', 'hoy'], ['Próxima', 'proximo']];
const ordenar = (lista, hoy) => [...lista].sort((a, b) => grupoDe(a, hoy) - grupoDe(b, hoy)
  || PRIORIDADES.indexOf(a.prioridad) - PRIORIDADES.indexOf(b.prioridad)
  || new Date(a.inicioAt) - new Date(b.inicioAt));

// ¿Cuándo tengo que atenderlo? Pagos a proveedores, depósito pendiente y tareas por fecha.
// Vista principal: calendario. "Vista de agenda" (?vista=lista): listado de actividades por periodo.
export default function Agenda() {
  const navigate = useNavigate(); // vencimientos de proveedores → Cuentas por pagar
  const { sucursalId, sucursales } = useSucursalActiva();
  const [params, setParams] = useSearchParams();
  const vistaLista = params.get('vista') === 'lista';
  const calendarRef = useRef(null);
  const [showDepositos, setShowDepositos] = useState(false);
  const [cierreId, setCierreId] = useState(null);

  const hoy = hoyGT();
  const [sel, setSel] = useState(() => leerPeriodo(hoy));
  const [verCompletadas, setVerCompletadas] = useState(false);
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { try { sessionStorage.setItem(CLAVE_PERIODO, JSON.stringify(sel)); } catch {} }, [sel]);

  const rango = useMemo(() => rangoPeriodo(sel.periodo, hoy, { desde: sel.desde, hasta: sel.hasta }), [sel, hoy]);
  const rangoInvalido = !rango.desde || !rango.hasta || rango.hasta < rango.desde;

  const cargarLista = useCallback(async () => {
    if (!vistaLista || !sucursalId || rangoInvalido) return;
    setCargando(true); setError('');
    try { setLista(await getTareasLista(sucursalId, rango.desde, rango.hasta, verCompletadas ? 'completadas' : 'pendientes')); }
    catch (e) { setLista([]); setError(e.message || 'No se pudieron cargar las actividades.'); }
    finally { setCargando(false); }
  }, [vistaLista, sucursalId, rango.desde, rango.hasta, rangoInvalido, verCompletadas]);
  useEffect(() => { cargarLista(); }, [cargarLista]);
  const recargar = () => { calendarRef.current?.reload?.(); cargarLista(); };

  const cambiarVista = () => setParams(vistaLista ? {} : { vista: 'lista' }, { replace: true });
  const elegirPeriodo = (periodo) => setSel((s) => (periodo === 'personalizado' && s.periodo !== 'personalizado'
    ? { periodo, ...rango } // parte del periodo que se estaba viendo
    : { ...s, periodo }));
  const cambiarFecha = (k, v) => v && setSel((s) => ({ ...s, [k]: v }));

  const cambiarEstado = async (t, completar) => {
    if (!completar) {
      const ok = await Swal.fire({ title: 'Reabrir actividad', text: `"${t.titulo}" volverá a pendientes activos.`, icon: 'question', showCancelButton: true, confirmButtonText: 'Reabrir' });
      if (!ok.isConfirmed) return;
    }
    try { await (completar ? completarTarea : reabrirTarea)(sucursalId, t.id); recargar(); }
    catch (e) { Swal.fire('Error', e.message || 'No se pudo actualizar la actividad.', 'error'); }
  };

  const filas = useMemo(() => (verCompletadas ? lista : ordenar(lista, hoy)), [lista, verCompletadas, hoy]);

  return (
    <div className="agenda-shell">
      <header className="agenda-header">
        <div><h1>Agenda</h1><p>{vistaLista ? 'Actividades del periodo, atrasadas primero.' : 'Vencimientos, depósitos y tareas por fecha.'}</p></div>
        <div className="agenda-acciones">
          <button type="button" className="fx-btn" onClick={cambiarVista}>{vistaLista ? 'Vista calendario' : 'Vista de agenda'}</button>
          <button type="button" className="fx-btn fx-btn--primary" onClick={() => calendarRef.current?.openAddModal?.()}>+ Añadir tarea</button>
        </div>
      </header>

      {vistaLista ? (
        <section className="fx-card agenda-lista" aria-label="Vista de agenda">
          <div className="agenda-periodo" role="group" aria-label="Periodo">
            <span className="agenda-periodo-titulo">Periodo</span>
            {PERIODOS.map(([id, label]) => (
              <button key={id} type="button" className={`fx-btn ${sel.periodo === id ? 'fx-btn--primary' : ''}`} aria-pressed={sel.periodo === id}
                onClick={() => elegirPeriodo(id)}>{label}</button>))}
          </div>
          <div className="agenda-periodo-rango">
            {sel.periodo === 'personalizado' ? <>
              <label>Desde <input type="date" value={sel.desde} onChange={(e) => cambiarFecha('desde', e.target.value)} /></label>
              <label>Hasta <input type="date" value={sel.hasta} min={sel.desde} onChange={(e) => cambiarFecha('hasta', e.target.value)} /></label>
            </> : <span>{rango.desde === rango.hasta ? fechaCorta(rango.desde) : `${fechaCorta(rango.desde)} – ${fechaCorta(rango.hasta)}`}</span>}
          </div>
          {rangoInvalido && <p role="alert" className="agenda-error">"Hasta" no puede ser anterior a "Desde".</p>}

          <div className="agenda-seguimiento-hd">
            <h2>{verCompletadas ? 'Completadas' : 'Pendientes'} <span className={`fx-badge fx-badge--${verCompletadas ? 'completado' : 'hoy'}`}>{rangoInvalido ? 0 : lista.length}</span></h2>
            <button type="button" className="fx-link" onClick={() => setVerCompletadas((v) => !v)}>{verCompletadas ? 'Ver pendientes' : 'Ver completadas'}</button>
          </div>
          {error && <p role="alert" className="agenda-error">{error}</p>}

          {rangoInvalido ? null : cargando ? <p className="fx-empty">Cargando…</p> : !filas.length ? (
            <p className="fx-empty">{verCompletadas ? 'No hay actividades completadas en este periodo.' : 'No hay actividades pendientes en este periodo.'}</p>
          ) : verCompletadas ? (
            <table className="fx-table fx-table--stack">
              <thead><tr><th>Actividad</th><th>Fecha</th><th>Finalizada</th><th>Por</th><th>Estado</th><th>Acción</th></tr></thead>
              <tbody>{filas.map((t) => (
                <tr key={t.id}>
                  <td data-label="Actividad"><b>{t.titulo}</b></td>
                  <td data-label="Fecha">{fechaCorta(fechaGT(t.inicioAt))}</td>
                  <td data-label="Finalizada">{fechaHora(t.completadoAt)}</td>
                  <td data-label="Por">{t.completadoPor || '—'}</td>
                  <td data-label="Estado"><span className="fx-badge fx-badge--completado">Completada</span></td>
                  <td data-label="Acción"><button type="button" className="fx-link" onClick={() => cambiarEstado(t, false)}>Reabrir</button></td>
                </tr>))}</tbody>
            </table>
          ) : (
            <table className="fx-table fx-table--stack">
              <thead><tr><th>Actividad</th><th>Fecha / Desde</th><th>Prioridad</th><th>Estado</th><th>Acción</th></tr></thead>
              <tbody>{filas.map((t) => {
                const [estado, clase] = ESTADO_GRUPO[grupoDe(t, hoy)];
                const hastaCompletar = t.seguimiento === 'hasta_completar';
                return (
                  <tr key={t.id}>
                    <td data-label="Actividad"><b>{t.titulo}</b>
                      {(t.descripcion || t.serieId) && <small className="fx-empty agenda-detalle">
                        {t.descripcion}{t.descripcion && t.serieId ? ' · ' : ''}{t.serieId ? `Se repite (${REPETICION[t.frecuencia] || 'serie'})` : ''}</small>}
                    </td>
                    <td data-label="Fecha / Desde">{hastaCompletar ? `Desde ${fechaCorta(fechaGT(t.inicioAt))}` : `${fechaCorta(fechaGT(t.inicioAt))} ${horaGT(t.inicioAt)}`}</td>
                    <td data-label="Prioridad">{t.prioridad}</td>
                    <td data-label="Estado"><span className={`fx-badge fx-badge--${clase}`}>{estado}</span>{t.estado === 'En curso' && <small className="fx-empty agenda-detalle">En curso</small>}</td>
                    <td data-label="Acción"><div className="agenda-fila-acciones">
                      <button type="button" className="fx-btn fx-btn--primary" onClick={() => cambiarEstado(t, true)}>✓ Completar</button>
                      <button type="button" className="fx-link" onClick={() => calendarRef.current?.openEditTarea?.(t)}>Editar</button>
                    </div></td>
                  </tr>);
              })}</tbody>
            </table>
          )}
        </section>
      ) : (
        <ul className="agenda-leyenda" aria-label="Leyenda">
          <li><i style={{ background: 'var(--info)' }} />Pago próximo</li>
          <li><i style={{ background: 'var(--danger)' }} />Pago vencido</li>
          <li><i style={{ background: 'var(--partial)' }} />Pago parcial</li>
          <li><i style={{ background: 'var(--ok-muted)' }} />Pagado</li>
          <li><i style={{ background: 'var(--event-deposit)' }} />Depósito</li>
          <li><i style={{ background: 'var(--event-review)' }} />Cuadre por revisar</li>
          <li><i style={{ background: 'var(--event-correction)' }} />Cuadre en corrección</li>
          <li><i style={{ background: 'var(--warn)' }} />Por liquidar</li>
          <li><i style={{ background: 'var(--event-task)' }} />Tarea</li>
        </ul>
      )}
      {/* El calendario contiene el formulario de actividades: en la Vista de agenda solo se oculta la grilla. */}
      <div className={vistaLista ? undefined : 'fx-card agenda-cal'}>
        <MyCalendar ref={calendarRef} sucursalId={sucursalId} sucursales={sucursales} oculto={vistaLista} onTareasChanged={cargarLista}
          onVerProveedor={(_, cuentaId) => cuentaId && navigate(`/Finanzas/CuentasPorPagar?ver=${cuentaId}`)} onVerDeposito={() => setShowDepositos(true)} onVerCierre={setCierreId} />
      </div>
      {cierreId && <CierreDetalleModal cierreId={cierreId} onClose={() => { setCierreId(null); recargar(); }} onChanged={recargar} />}
      {showDepositos && <DepositosModal sucursalId={sucursalId} isAdmin onClose={() => { setShowDepositos(false); recargar(); }} onSaved={recargar} />}
    </div>
  );
}
